import { IMainDetailsForm, IProductRelatedInformationForm } from '@/types/active-projects/MainDetails.types';
import { getMainDetailsPermissions, isFieldEditable } from '@/static/permissions/active-projects/MainDetails.permissions';
import { MainDetailsFormInitial } from '@/static/initial/active-projects/MainDetails';
import {
  normalizeCertifiedOrganicFromApi,
  resolveCertifiedOrganicFromApiResponse,
  resolveProduceRacPricingFieldsFromApiResponse,
} from '@/components/ActiveProjects/MainDetails/utils/produceRacMainDetailsHelper';
import {
  parseActiveProjectMultiSelectIds,
  formatActiveProjectMultiSelectIdsForSave,
} from '@/components/ActiveProjects/utils/helper';

export const giftPackToYesNo = (value: unknown): "yes" | "no" =>
  value === true || value === "true" ? "yes" : "no";

/** API yes/no ("Yes", "No", booleans, etc.) → radio form value ("yes" / "no" / ""). */
export const normalizeYesNoRadioFromApi = (value: unknown): "yes" | "no" | "" => {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "yes") {
    return "yes";
  }
  if (normalized === "no") {
    return "no";
  }
  return "";
};

/** Legacy Incomplete defaults to "no" when API has no value. */
export const normalizeLegacyIncompleteFromApi = (value: unknown): "yes" | "no" =>
  normalizeYesNoRadioFromApi(value) || "no";

export interface FlattenedResponseData {
    // ProductSpecificationInformation fields
    specificationVersion?: string;
    productSpecificationType?: string;
    productSpecificationName?: string;
    artworkDeadline?: string;
    artworkProjectId?: string;
    skuNumber?: string;
    
    // ProductRelatedInformation fields
    sku?: string;
    productName?: string;
    giftPack?: boolean;
    isMultiPackProduct?: string;
    legacyIncomplete?: string | null;
    numberOfFlavorsVarieties?: string;
    weightVolumePackQty?: string;
    weightUOM?: string;
    brandType?: string;
    category?: string;
    businessCategory?: {
        id: string;
        categoryId: string;
        description: string;
        categoryType: string;
        leader: string;
        manager: string | null;
    };
    // Personnel fields - now objects with id and name
    categoryManager?: {
        id: string;
        name: string;
    };
    buyer?: {
        id: string;
        name: string;
    };
    trafficManager?: {
        id: string;
        name: string;
    };
    qaComplianceManager?: {
        id: string;
        name: string;
    };
    productDeveloper?: {
        id: string;
        name: string;
    };
    nutritionist?: {
        id: string;
        name: string;
    };
    regulatoryAgenciesOversight?: string | string[];  // API returns array; we send comma-separated string
    foodSaftyHaccpPlan?: string;
    productOnFLTlist?: string;
    ftlList?: string;
    warehousesWhereSupplied?: string | string[];
    projectType?: string;
    launchDate?: string;
    regulatoryGovernance?: string;
    nationalDrugCodeNumber?: string;
    certifiedOrganic?: string;
    retailPrice?: string | number;
    size?: string | number;
    packSize?: string | number;
    opportunityBuy?: string | null;
    versionNumber?: string;
    // VendorRelatedInformation fields
    vendorNumber?: string;
    vendorName?: string;
    sites?: any[];
    
    // VendorFacilityInformation fields - now an array
    vendorFacilityInformation?: Array<{
        siteName: string;
        siteCountry: string;
        siteAddress: string;
        siteCity: string;
        siteState: string;
        siteZipcode: string;
    }>;
    
    // Photos of Item in Retail Packaging - new structure
    photosofItemInRetailPackaging?: {
        retailPackaging: {
            id: string;
            fileName: string;
        };
        outOfPackaging: {
            id: string;
            fileName: string;
        };
        prepared: {
            id: string;
            fileName: string;
        };
        shelfLifeStudy: {
            id: string;
            fileName: string;
        };
    };
    
    // Notes for revision
    revisionNotes?: string;
    
    // Legacy fields for backward compatibility
    siteCode?: string;
    siteName?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCodePostalCode?: string;
    
    // Metadata
    productId?: string;
    variantId?: string;
    lastUpdated?: string;
    updatedBy?: string;
  }
/**
 * Types for editable-only sections
 */
export interface EditableProductSpecificationInformation {
  productSpecificationName: string;
  artworkDeadline: string;
}

export interface EditableProductRelatedInformation {
  weightVolumePackQty: string;
  weightUOM: string;
  buyer: string;
  trafficManager: string;
  regulatoryAgenciesOversight: string | string[];  // form/API array; sent as comma-separated string
  foodSaftyHaccpPlan: string;
  ftlList: string;
  warehousesWhereSupplied: string | string[];
}

/**
 * Structure for editable-only MainDetails form data
 */
export interface EditableMainDetailsForm {
  productSpecificationInformation?: EditableProductSpecificationInformation;
  productRelatedInformation?: EditableProductRelatedInformation;
  // No vendorRelatedInformation or vendorFacilityInformation as they have no editable fields
}

/**
 * Convert flattened API response data to full structured form - ALL FIELDS
 * This preserves all data from API response, both editable and non-editable
 */
export const convertFlattenedToStructuredFormAllFields = (
  flattenedData: FlattenedResponseData
): IMainDetailsForm => {
  
  console.log('🔄 [PayloadUtils] Converting flattened data to structured form (ALL FIELDS):', flattenedData);
  
  // Check if we have any actual data to convert
  const hasAnyData = Object.values(flattenedData).some(value => value !== undefined && value !== null && value !== '');
  console.log('📊 [PayloadUtils] API response has data?', hasAnyData);
  
  const structuredForm: IMainDetailsForm = {
    productSpecificationInformation: {
      specificationVersion: flattenedData.specificationVersion ?? '',
      versionNumber: flattenedData.versionNumber ?? '1',
      productSpecificationType: flattenedData.productSpecificationType ?? '',
      productSpecificationName: flattenedData.productSpecificationName ?? '',
      artworkDeadline: flattenedData.artworkDeadline ?? '',
      artworkProjectId: flattenedData.artworkProjectId ?? '',
      opportunityBuy: flattenedData.opportunityBuy ?? ''
    },
    
    productRelatedInformation: {
      sku: flattenedData.sku ?? '',
      productName: flattenedData.productName ?? '',
      legacyIncomplete: normalizeLegacyIncompleteFromApi(flattenedData.legacyIncomplete),
      isMultiPackProduct:
        flattenedData.isMultiPackProduct ??
        (flattenedData.giftPack !== undefined
          ? giftPackToYesNo(flattenedData.giftPack)
          : ""),
      numberOfFlavorsVarieties: flattenedData.numberOfFlavorsVarieties ?? '',
      weightVolumePackQty: flattenedData.weightVolumePackQty ?? '',
      weightUOM: flattenedData.weightUOM ?? '',
      brandType: flattenedData.brandType ?? '',
      category: flattenedData.businessCategory?.description ?? flattenedData.category ?? '',
      categoryManager: (typeof flattenedData.categoryManager === 'object' ? flattenedData.categoryManager?.name : flattenedData.categoryManager) ?? '',
      buyer: (typeof flattenedData.buyer === 'object' ? flattenedData.buyer?.name : flattenedData.buyer) ?? '',
      trafficManager: (typeof flattenedData.trafficManager === 'object' ? flattenedData.trafficManager?.name : flattenedData.trafficManager) ?? '',
      qaComplianceManager: (typeof flattenedData.qaComplianceManager === 'object' ? flattenedData.qaComplianceManager?.name : flattenedData.qaComplianceManager) ?? '',
      productCreator: (typeof flattenedData.productDeveloper === 'object' ? flattenedData.productDeveloper?.name : flattenedData.productDeveloper) ?? '',
      nutritionist: (typeof flattenedData.nutritionist === 'object' ? flattenedData.nutritionist?.name : flattenedData.nutritionist) ?? '',
      regulatoryAgenciesOversight: parseActiveProjectMultiSelectIds(flattenedData.regulatoryAgenciesOversight),
      foodSaftyHaccpPlan: flattenedData.foodSaftyHaccpPlan ?? '',
      ftlList: flattenedData.ftlList ?? '',
      warehousesWhereSupplied: parseActiveProjectMultiSelectIds(flattenedData.warehousesWhereSupplied),
      projectType: flattenedData.projectType ?? '',
      launchDate: flattenedData.launchDate ?? '',
      regulatoryGovernance: flattenedData.regulatoryGovernance ?? '',
      nationalDrugCodeNumber: flattenedData.nationalDrugCodeNumber ?? '',
      certifiedOrganic: normalizeCertifiedOrganicFromApi(
        flattenedData.certifiedOrganic ??
          (flattenedData as { vendorRelatedInformation?: { certifiedOrganic?: unknown } })
            .vendorRelatedInformation?.certifiedOrganic
      ) ?? '',
      retailPrice: resolveProduceRacPricingFieldsFromApiResponse(flattenedData).retailPrice,
      productSize: resolveProduceRacPricingFieldsFromApiResponse(flattenedData).productSize,
      casePack: resolveProduceRacPricingFieldsFromApiResponse(flattenedData).casePack,
    },
    
    vendorRelatedInformation: {
      vendorNumber: flattenedData.vendorNumber ?? '',
      vendorName: flattenedData.vendorName ?? '',
      sites: Array.isArray(flattenedData?.sites) ? flattenedData?.sites?.join(', ') : (flattenedData?.sites ?? '')
    },
    
    vendorFacilityInformation: [{
      siteCode: flattenedData.siteCode ?? '',
      siteName: flattenedData.siteName ?? '',
      address1: flattenedData.address1 ?? '',
      address2: flattenedData.address2 ?? '',
      city: flattenedData.city ?? '',
      state: flattenedData.state ?? '',
      country: flattenedData.country ?? '',
      zipCodePostalCode: flattenedData.zipCodePostalCode ?? ''
    }],
    photosofItemInRetailPackaging: {},
    revisionNotes: flattenedData.revisionNotes ?? ''
  };
  
  console.log('✅ [PayloadUtils] Structured form (ALL FIELDS) created:', structuredForm);
  
  return structuredForm;
};

/**
 * Convert flattened API response data to structured form - EDITABLE FIELDS ONLY
 * This only includes fields that can be edited by the user
 */
export const convertFlattenedToStructuredFormEditableOnly = (
  flattenedData: FlattenedResponseData
): EditableMainDetailsForm => {
  
  console.log('🔄 [PayloadUtils] Converting flattened data to structured form (EDITABLE FIELDS ONLY):', flattenedData);
  
  // Check if we have any editable data to convert
  const hasEditableData = !!(
    flattenedData.productSpecificationName ||
    flattenedData.artworkDeadline || 
    flattenedData.weightVolumePackQty || 
    flattenedData.weightUOM || 
    flattenedData.buyer || 
    flattenedData.trafficManager || 
    flattenedData.regulatoryAgenciesOversight || 
    flattenedData.foodSaftyHaccpPlan || 
    flattenedData.ftlList || 
    flattenedData.warehousesWhereSupplied
  );
  console.log('📊 [PayloadUtils] API response has editable data?', hasEditableData);
  
  const editableFieldsFound = [];
  if (flattenedData.productSpecificationName) editableFieldsFound.push('productSpecificationName');
  if (flattenedData.artworkDeadline) editableFieldsFound.push('artworkDeadline');
  if (flattenedData.weightVolumePackQty) editableFieldsFound.push('weightVolumePackQty');
  if (flattenedData.weightUOM) editableFieldsFound.push('weightUOM');
  if (flattenedData.buyer) editableFieldsFound.push('buyer');
  if (flattenedData.regulatoryAgenciesOversight) editableFieldsFound.push('regulatoryAgenciesOversight');
  if (flattenedData.foodSaftyHaccpPlan) editableFieldsFound.push('foodSaftyHaccpPlan');
  if (flattenedData.ftlList) editableFieldsFound.push('ftlList');
  if (flattenedData.warehousesWhereSupplied) editableFieldsFound.push('warehousesWhereSupplied');
  console.log('📊 [PayloadUtils] Editable fields found in response:', editableFieldsFound);
  
  const structuredForm: EditableMainDetailsForm = {};
  
  // ProductSpecificationInformation - ONLY editable fields
  const productSpecInfo: Partial<EditableProductSpecificationInformation> = {};
  
  if (flattenedData.productSpecificationName !== undefined && flattenedData.productSpecificationName !== null) {
    productSpecInfo.productSpecificationName = flattenedData.productSpecificationName;
  }
  if (flattenedData.artworkDeadline !== undefined && flattenedData.artworkDeadline !== null) {
    productSpecInfo.artworkDeadline = flattenedData.artworkDeadline;
  }
  
  // Only add section if it has data
  if (Object.keys(productSpecInfo).length > 0) {
    structuredForm.productSpecificationInformation = productSpecInfo as EditableProductSpecificationInformation;
  }
  
  // ProductRelatedInformation - ONLY editable fields
  const productRelatedInfo: Partial<EditableProductRelatedInformation> = {};
  
  if (flattenedData.weightVolumePackQty !== undefined && flattenedData.weightVolumePackQty !== null) {
    productRelatedInfo.weightVolumePackQty = flattenedData.weightVolumePackQty;
  }
  if (flattenedData.weightUOM !== undefined && flattenedData.weightUOM !== null) {
    productRelatedInfo.weightUOM = flattenedData.weightUOM;
  }
  if (flattenedData.buyer !== undefined && flattenedData.buyer !== null) {
    productRelatedInfo.buyer = typeof flattenedData.buyer === 'object' ? flattenedData.buyer.name : flattenedData.buyer;
  }
  if (flattenedData.trafficManager !== undefined && flattenedData.trafficManager !== null) {
    productRelatedInfo.trafficManager = typeof flattenedData.trafficManager === 'object' ? flattenedData.trafficManager.name : flattenedData.trafficManager;
  }
  if (flattenedData.regulatoryAgenciesOversight !== undefined && flattenedData.regulatoryAgenciesOversight !== null) {
    productRelatedInfo.regulatoryAgenciesOversight = parseActiveProjectMultiSelectIds(
      flattenedData.regulatoryAgenciesOversight
    );
  }
  if (flattenedData.foodSaftyHaccpPlan !== undefined && flattenedData.foodSaftyHaccpPlan !== null) {
    productRelatedInfo.foodSaftyHaccpPlan = flattenedData.foodSaftyHaccpPlan;
  }
  if (flattenedData.ftlList !== undefined && flattenedData.ftlList !== null) {
    productRelatedInfo.ftlList = flattenedData.ftlList;
  }
  if (flattenedData.warehousesWhereSupplied !== undefined && flattenedData.warehousesWhereSupplied !== null) {
    productRelatedInfo.warehousesWhereSupplied = parseActiveProjectMultiSelectIds(
      flattenedData.warehousesWhereSupplied
    );
  }
  
  // Only add section if it has data
  if (Object.keys(productRelatedInfo).length > 0) {
    structuredForm.productRelatedInformation = productRelatedInfo as EditableProductRelatedInformation;
  }
  
  console.log('✅ [PayloadUtils] Structured form (EDITABLE FIELDS ONLY) created:', structuredForm);
  
  return structuredForm;
};

/**
 * Merge editable fields into existing MainDetails form - SELECTIVE FIELD UPDATE
 * Only updates fields that actually have values, preserves all existing fields
 */
export const mergeEditableFieldsIntoMainDetailsForm = (
  currentForm: IMainDetailsForm,
  editableFields: EditableMainDetailsForm
): IMainDetailsForm => {
  
  console.log('🔄 [PayloadUtils] Merging editable fields into existing form:', { currentForm, editableFields });
  
  const updatedForm: IMainDetailsForm = {
    // ProductSpecificationInformation - selective merge
    productSpecificationInformation: {
      ...currentForm.productSpecificationInformation,
      // Only update productSpecificationName if it exists in editable fields
      ...(editableFields.productSpecificationInformation?.productSpecificationName !== undefined && 
          editableFields.productSpecificationInformation?.productSpecificationName !== null && {
        productSpecificationName: editableFields.productSpecificationInformation.productSpecificationName
      }),
      // Only update artworkDeadline if it exists in editable fields
      ...(editableFields.productSpecificationInformation?.artworkDeadline !== undefined && 
          editableFields.productSpecificationInformation?.artworkDeadline !== null && {
        artworkDeadline: editableFields.productSpecificationInformation.artworkDeadline
      })
    },
    
    // ProductRelatedInformation - selective merge of individual fields
    productRelatedInformation: {
      ...currentForm.productRelatedInformation,
      // Only update fields that exist in editable fields (checking for both undefined and null)
      ...(editableFields.productRelatedInformation?.weightVolumePackQty !== undefined && 
          editableFields.productRelatedInformation?.weightVolumePackQty !== null && {
        weightVolumePackQty: editableFields.productRelatedInformation.weightVolumePackQty
      }),
      ...(editableFields.productRelatedInformation?.weightUOM !== undefined && 
          editableFields.productRelatedInformation?.weightUOM !== null && {
        weightUOM: editableFields.productRelatedInformation.weightUOM
      }),
      ...(editableFields.productRelatedInformation?.buyer !== undefined && 
          editableFields.productRelatedInformation?.buyer !== null && {
        buyer: editableFields.productRelatedInformation.buyer
      }),
      ...(editableFields.productRelatedInformation?.regulatoryAgenciesOversight !== undefined && 
          editableFields.productRelatedInformation?.regulatoryAgenciesOversight !== null && {
        regulatoryAgenciesOversight: editableFields.productRelatedInformation.regulatoryAgenciesOversight
      }),
      ...(editableFields.productRelatedInformation?.foodSaftyHaccpPlan !== undefined && 
          editableFields.productRelatedInformation?.foodSaftyHaccpPlan !== null && {
        foodSaftyHaccpPlan: editableFields.productRelatedInformation.foodSaftyHaccpPlan
      }),
      ...(editableFields.productRelatedInformation?.ftlList !== undefined && 
          editableFields.productRelatedInformation?.ftlList !== null && {
        ftlList: editableFields.productRelatedInformation.ftlList
      }),
      ...(editableFields.productRelatedInformation?.warehousesWhereSupplied !== undefined && 
          editableFields.productRelatedInformation?.warehousesWhereSupplied !== null && {
        warehousesWhereSupplied: editableFields.productRelatedInformation.warehousesWhereSupplied
      })
    },
    
    // Keep existing vendor sections completely unchanged
    vendorRelatedInformation: {
      ...currentForm.vendorRelatedInformation
    },
    
    vendorFacilityInformation: currentForm.vendorFacilityInformation || [],
    photosofItemInRetailPackaging: currentForm.photosofItemInRetailPackaging || {}
  };
  
  console.log('✅ [PayloadUtils] Merged form created (selective update):', updatedForm);
  
  return updatedForm;
};

/**
 * Comprehensive function to merge MainDetails form from flattened API response
 * Handles both initial load (all fields) and updates (selective merge)
 */
export const mergeMainDetailsFormFromFlattenedResponse = (
  currentForm: IMainDetailsForm,
  flattenedResponse: FlattenedResponseData,
  options: {
    forceFullUpdate?: boolean;
    preserveExistingData?: boolean;
    onlyEditableFields?: boolean;
  } = {}
): IMainDetailsForm => {
  
  console.log('🔄 [PayloadUtils] Merging MainDetails form from flattened response:', {
    currentForm,
    flattenedResponse,
    options
  });
  
  const {
    forceFullUpdate = false,
    preserveExistingData = true,
    onlyEditableFields = false
  } = options;
  
  // Check if current form has actual data (not just empty initial values)
  const hasExistingData = currentForm && Object.values(currentForm).some(section => 
    section && Object.values(section).some(value => 
      value && typeof value === 'string' && value.trim() !== ''
    )
  );
  
  console.log('📊 [PayloadUtils] Current form has existing data:', hasExistingData);
  
  // Determine merge strategy
  let mergeStrategy: 'full' | 'selective' | 'editable-only';
  
  if (forceFullUpdate) {
    mergeStrategy = 'full';
  } else if (onlyEditableFields || (hasExistingData && preserveExistingData)) {
    mergeStrategy = onlyEditableFields ? 'editable-only' : 'selective';
  } else {
    mergeStrategy = 'full';
  }
  
  console.log('🎯 [PayloadUtils] Using merge strategy:', mergeStrategy);
  
  // Helper function to safely get value from flattened response
  const getValue = (field: keyof FlattenedResponseData, fallback: string = ''): string => {
    const value = flattenedResponse[field];
    if (value === undefined || value === null) {
      return fallback;
    }
    if (typeof value === 'string') {
      return value;
    }
    return String(value) || fallback;
  };
  
  // Helper function to conditionally update field
  const conditionalUpdate = (
    responseValue: any,
    currentValue: string,
    isEditable: boolean = true
  ): string => {
    if (mergeStrategy === 'full') {
      return typeof responseValue === 'string' ? responseValue : (responseValue || currentValue);
    }
    
    if (mergeStrategy === 'editable-only' && !isEditable) {
      return currentValue; // Keep existing value for non-editable fields
    }
    
    if (mergeStrategy === 'selective' && responseValue !== undefined && responseValue !== null) {
      return typeof responseValue === 'string' ? responseValue : String(responseValue || '');
    }
    
    return currentValue; // Keep existing value if no update needed
  };
  
  // Create merged form
  const mergedForm: IMainDetailsForm = {
    // ProductSpecificationInformation
    productSpecificationInformation: {
      specificationVersion: conditionalUpdate(
        flattenedResponse.specificationVersion,
        currentForm?.productSpecificationInformation?.specificationVersion || '',
        false // non-editable
      ),
      versionNumber: conditionalUpdate(
        flattenedResponse.versionNumber,
        currentForm?.productSpecificationInformation?.versionNumber || '1',
        false // non-editable (display only)
      ),
      productSpecificationType: conditionalUpdate(
        flattenedResponse.productSpecificationType,
        currentForm?.productSpecificationInformation?.productSpecificationType || '',
        false // non-editable
      ),
      productSpecificationName: currentForm?.productSpecificationInformation?.productSpecificationName || '', // Never auto-populate from API
      artworkDeadline: conditionalUpdate(
        flattenedResponse.artworkDeadline,
        currentForm?.productSpecificationInformation?.artworkDeadline || '',
        true // editable
      ),
      artworkProjectId: conditionalUpdate(
        flattenedResponse.artworkProjectId,
        currentForm?.productSpecificationInformation?.artworkProjectId || '',
        false // non-editable (system generated)
      )
    },
    
    // ProductRelatedInformation
    productRelatedInformation: {
      sku: conditionalUpdate(
        flattenedResponse.sku,
        currentForm?.productRelatedInformation?.sku || '',
        false // non-editable
      ),
      productName: conditionalUpdate(
        flattenedResponse.productName,
        currentForm?.productRelatedInformation?.productName || '',
        false // non-editable
      ),
      legacyIncomplete: conditionalUpdate(
        normalizeLegacyIncompleteFromApi(flattenedResponse.legacyIncomplete),
        currentForm?.productRelatedInformation?.legacyIncomplete || 'no',
        true // editable
      ),
      isMultiPackProduct: conditionalUpdate(
        flattenedResponse.isMultiPackProduct ??
          giftPackToYesNo(flattenedResponse.giftPack),
        currentForm?.productRelatedInformation?.isMultiPackProduct || '',
        true // editable
      ),
      numberOfFlavorsVarieties: conditionalUpdate(
        flattenedResponse.numberOfFlavorsVarieties,
        currentForm?.productRelatedInformation?.numberOfFlavorsVarieties || '',
        true // editable
      ),
      weightVolumePackQty: conditionalUpdate(
        flattenedResponse.weightVolumePackQty,
        currentForm?.productRelatedInformation?.weightVolumePackQty || '',
        true // editable
      ),
      weightUOM: conditionalUpdate(
        flattenedResponse.weightUOM,
        currentForm?.productRelatedInformation?.weightUOM || '',
        true // editable
      ),
      brandType: conditionalUpdate(
        flattenedResponse.brandType,
        currentForm?.productRelatedInformation?.brandType || '',
        false // non-editable
      ),
      category: conditionalUpdate(
        flattenedResponse.category,
        currentForm?.productRelatedInformation?.category || '',
        false // non-editable
      ),
      categoryManager: conditionalUpdate(
        typeof flattenedResponse.categoryManager === 'object' ? flattenedResponse.categoryManager?.name : flattenedResponse.categoryManager,
        currentForm?.productRelatedInformation?.categoryManager || '',
        false // non-editable
      ),
      buyer: conditionalUpdate(
        typeof flattenedResponse.buyer === 'object' ? flattenedResponse.buyer?.name : flattenedResponse.buyer,
        currentForm?.productRelatedInformation?.buyer || '',
        true // editable
      ),
      trafficManager: conditionalUpdate(
        typeof flattenedResponse.trafficManager === 'object' ? flattenedResponse.trafficManager?.name : flattenedResponse.trafficManager,
        currentForm?.productRelatedInformation?.trafficManager || '',
        true // editable
      ),
      qaComplianceManager: conditionalUpdate(
        typeof flattenedResponse.qaComplianceManager === 'object' ? flattenedResponse.qaComplianceManager?.name : flattenedResponse.qaComplianceManager,
        currentForm?.productRelatedInformation?.qaComplianceManager || '',
        false // non-editable
      ),
      productCreator: conditionalUpdate(
        typeof flattenedResponse.productDeveloper === 'object' ? flattenedResponse.productDeveloper?.name : flattenedResponse.productDeveloper,
        currentForm?.productRelatedInformation?.productCreator || '',
        false // non-editable
      ),
      nutritionist: conditionalUpdate(
        typeof flattenedResponse.nutritionist === 'object' ? flattenedResponse.nutritionist?.name : flattenedResponse.nutritionist,
        currentForm?.productRelatedInformation?.nutritionist || '',
        false // non-editable
      ),
      regulatoryAgenciesOversight: (() => {
        const responseArr = parseActiveProjectMultiSelectIds(flattenedResponse.regulatoryAgenciesOversight);
        const currentArr = parseActiveProjectMultiSelectIds(
          currentForm?.productRelatedInformation?.regulatoryAgenciesOversight
        );
        if (mergeStrategy === 'full') return responseArr.length ? responseArr : currentArr;
        if (mergeStrategy === 'editable-only') return responseArr.length ? responseArr : currentArr;
        if (mergeStrategy === 'selective' && responseArr.length > 0) return responseArr;
        return currentArr;
      })(),
      foodSaftyHaccpPlan: conditionalUpdate(
        flattenedResponse.foodSaftyHaccpPlan,
        currentForm?.productRelatedInformation?.foodSaftyHaccpPlan || '',
        true // editable
      ),
      ftlList: conditionalUpdate(
        flattenedResponse.ftlList,
        currentForm?.productRelatedInformation?.ftlList || '',
        true // editable
      ),
      warehousesWhereSupplied: conditionalUpdate(
        Array.isArray(flattenedResponse.warehousesWhereSupplied)
          ? flattenedResponse.warehousesWhereSupplied.join(',')
          : flattenedResponse.warehousesWhereSupplied,
        Array.isArray(currentForm?.productRelatedInformation?.warehousesWhereSupplied)
          ? currentForm.productRelatedInformation.warehousesWhereSupplied.join(',')
          : (currentForm?.productRelatedInformation?.warehousesWhereSupplied || ''),
        true // editable
      ),
      projectType: conditionalUpdate(
        flattenedResponse.projectType,
        currentForm?.productRelatedInformation?.projectType || '',
        true // editable (Only TJ)
      ),
      launchDate: conditionalUpdate(
        flattenedResponse.launchDate,
        currentForm?.productRelatedInformation?.launchDate || '',
        true // editable (Only TJ)
      ),
      regulatoryGovernance: conditionalUpdate(
        flattenedResponse.regulatoryGovernance,
        currentForm?.productRelatedInformation?.regulatoryGovernance || '',
        true // editable (Only TJ)
      ),
      nationalDrugCodeNumber: conditionalUpdate(
        flattenedResponse.nationalDrugCodeNumber,
        currentForm?.productRelatedInformation?.nationalDrugCodeNumber || '',
        true // editable (Both TJ and Vendor, HABA only)
      ),
      certifiedOrganic: conditionalUpdate(
        resolveCertifiedOrganicFromApiResponse(flattenedResponse),
        currentForm?.productRelatedInformation?.certifiedOrganic || '',
        true // editable (Produce RAC only)
      ),
      retailPrice: conditionalUpdate(
        resolveProduceRacPricingFieldsFromApiResponse(flattenedResponse).retailPrice,
        currentForm?.productRelatedInformation?.retailPrice || '',
        true // editable (Produce RAC pricing — TJ only)
      ),
      productSize: conditionalUpdate(
        resolveProduceRacPricingFieldsFromApiResponse(flattenedResponse).productSize,
        currentForm?.productRelatedInformation?.productSize || '',
        true // editable (Produce RAC pricing — TJ only)
      ),
      casePack: conditionalUpdate(
        resolveProduceRacPricingFieldsFromApiResponse(flattenedResponse).casePack,
        currentForm?.productRelatedInformation?.casePack || '',
        true // editable (Produce RAC pricing — TJ only)
      )
    },
    
    // VendorRelatedInformation
    vendorRelatedInformation: {
      vendorNumber: conditionalUpdate(
        flattenedResponse.vendorNumber,
        currentForm?.vendorRelatedInformation?.vendorNumber || '',
        false // non-editable
      ),
      vendorName: conditionalUpdate(
        flattenedResponse.vendorName,
        currentForm?.vendorRelatedInformation?.vendorName || '',
        false // non-editable
      ),
      sites: conditionalUpdate(
        Array.isArray(flattenedResponse?.sites) ? flattenedResponse?.sites?.join(', ') : flattenedResponse?.sites,
        Array.isArray(currentForm?.vendorRelatedInformation?.sites)
          ? currentForm?.vendorRelatedInformation?.sites?.join(', ')
          : (currentForm?.vendorRelatedInformation?.sites || ''),
        false // non-editable
      )
    },
    
    // VendorFacilityInformation - handle as array, merge with existing facilities
    vendorFacilityInformation: (() => {
      const existingFacilities = currentForm?.vendorFacilityInformation || [];
      const newFacility = {
        siteCode: conditionalUpdate(
          flattenedResponse.siteCode,
          existingFacilities[0]?.siteCode || '',
          false // non-editable (siteCode is not editable per permissions)
        ),
        siteName: conditionalUpdate(
          flattenedResponse.siteName,
          existingFacilities[0]?.siteName || '',
          true // editable per permissions
        ),
        address1: conditionalUpdate(
          flattenedResponse.address1,
          existingFacilities[0]?.address1 || '',
          true // editable per permissions
        ),
        address2: conditionalUpdate(
          flattenedResponse.address2,
          existingFacilities[0]?.address2 || '',
          false // non-editable per permissions
        ),
        city: conditionalUpdate(
          flattenedResponse.city,
          existingFacilities[0]?.city || '',
          true // editable per permissions
        ),
        state: conditionalUpdate(
          flattenedResponse.state,
          existingFacilities[0]?.state || '',
          true // editable per permissions
        ),
        country: conditionalUpdate(
          flattenedResponse.country,
          existingFacilities[0]?.country || '',
          true // editable per permissions
        ),
        zipCodePostalCode: conditionalUpdate(
          flattenedResponse.zipCodePostalCode,
          existingFacilities[0]?.zipCodePostalCode || '',
          true // editable per permissions
        )
      };
      
      // If there are existing facilities, update the first one and keep the rest
      if (existingFacilities.length > 0) {
        return [newFacility, ...existingFacilities.slice(1)];
      }
      
      // If no existing facilities, create a new array with the facility
      return [newFacility];
    })(),
    photosofItemInRetailPackaging: currentForm?.photosofItemInRetailPackaging || {}
  };
  
  // Log summary of changes
  const changedFields: string[] = [];
  
  // Check ProductSpecificationInformation changes
  if (mergedForm.productSpecificationInformation.artworkDeadline !== currentForm?.productSpecificationInformation?.artworkDeadline) {
    changedFields.push('artworkDeadline');
  }
  
  // Check ProductRelatedInformation changes
  const productRelatedFields: (keyof IProductRelatedInformationForm)[] = ['weightVolumePackQty', 'weightUOM', 'buyer', 'trafficManager', 'regulatoryAgenciesOversight', 'foodSaftyHaccpPlan', 'ftlList', 'warehousesWhereSupplied', 'certifiedOrganic', 'retailPrice', 'productSize', 'casePack'];
  productRelatedFields.forEach(field => {
    if (mergedForm.productRelatedInformation[field] !== currentForm?.productRelatedInformation?.[field]) {
      changedFields.push(field);
    }
  });
  
  console.log('📊 [PayloadUtils] Fields updated from API response:', changedFields);
  console.log('✅ [PayloadUtils] Merged MainDetails form created:', mergedForm);
  
  return mergedForm;
};

/**
 * Dynamic merge function using permissions configuration
 * Automatically determines which fields are editable based on MainDetailsPermissions
 */
export const mergeMainDetailsFormUsingPermissions = (
  currentForm: IMainDetailsForm,
  flattenedResponse: FlattenedResponseData,
  options: {
    updateOnlyEditableFields?: boolean;
    preserveExistingData?: boolean;
    useInitialForMissing?: boolean;
    userModifiedFields?: Set<string>;
  } = {}
): IMainDetailsForm => {
  
  const {
    updateOnlyEditableFields = true,
    preserveExistingData = true,
    useInitialForMissing = false,
    userModifiedFields = new Set()
  } = options;
  
  console.log('🔄 [PayloadUtils] Merging MainDetails using permissions configuration:', {
    currentForm,
    flattenedResponse,
    options
  });
  
  // Create a copy of the current form
  const mergedForm: IMainDetailsForm = JSON.parse(JSON.stringify(currentForm));
  const updatedFields: string[] = [];

  const specificationType =
    flattenedResponse.productSpecificationType ??
    currentForm?.productSpecificationInformation?.productSpecificationType;
  
  // Helper function to extract string value from personnel object or return string directly
  const extractPersonnelValue = (value: any): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object' && value.name) {
      return value.name;
    }
    if (value && typeof value === 'object' && value.id) {
      return value.id;
    }
    return '';
  };

  // Mapping of flattened field names to their section and field names
  const fieldMapping: Record<string, { section: keyof IMainDetailsForm; field: string; isPersonnel?: boolean }> = {
    // ProductSpecificationInformation
    specificationVersion: { section: 'productSpecificationInformation', field: 'specificationVersion' },
    versionNumber: { section: 'productSpecificationInformation', field: 'versionNumber' },
    productSpecificationType: { section: 'productSpecificationInformation', field: 'productSpecificationType' },
    productSpecificationName: { section: 'productSpecificationInformation', field: 'productSpecificationName' },
    artworkDeadline: { section: 'productSpecificationInformation', field: 'artworkDeadline' },
    artworkProjectId: { section: 'productSpecificationInformation', field: 'artworkProjectId' },
    opportunityBuy: { section: 'productSpecificationInformation', field: 'opportunityBuy' },
    
    // ProductRelatedInformation
    sku: { section: 'productRelatedInformation', field: 'sku' },
    productName: { section: 'productRelatedInformation', field: 'productName' },
    weightVolumePackQty: { section: 'productRelatedInformation', field: 'weightVolumePackQty' },
    weightUOM: { section: 'productRelatedInformation', field: 'weightUOM' },
    brandType: { section: 'productRelatedInformation', field: 'brandType' },
    category: { section: 'productRelatedInformation', field: 'category' },
    categoryManager: { section: 'productRelatedInformation', field: 'categoryManager', isPersonnel: true },
    buyer: { section: 'productRelatedInformation', field: 'buyer', isPersonnel: true },
    qaComplianceManager: { section: 'productRelatedInformation', field: 'qaComplianceManager', isPersonnel: true },
    productCreator: { section: 'productRelatedInformation', field: 'productCreator', isPersonnel: true },
    productDeveloper: { section: 'productRelatedInformation', field: 'productDeveloper', isPersonnel: true },
    trafficManager: { section: 'productRelatedInformation', field: 'trafficManager', isPersonnel: true },
    nutritionist: { section: 'productRelatedInformation', field: 'nutritionist', isPersonnel: true },
    regulatoryAgenciesOversight: { section: 'productRelatedInformation', field: 'regulatoryAgenciesOversight' },
    foodSaftyHaccpPlan: { section: 'productRelatedInformation', field: 'foodSaftyHaccpPlan' },
    ftlList: { section: 'productRelatedInformation', field: 'ftlList' },
    warehousesWhereSupplied: { section: 'productRelatedInformation', field: 'warehousesWhereSupplied' },
    projectType: { section: 'productRelatedInformation', field: 'projectType' },
    launchDate: { section: 'productRelatedInformation', field: 'launchDate' },
    regulatoryGovernance: { section: 'productRelatedInformation', field: 'regulatoryGovernance' },
    nationalDrugCodeNumber: { section: 'productRelatedInformation', field: 'nationalDrugCodeNumber' },
    certifiedOrganic: { section: 'productRelatedInformation', field: 'certifiedOrganic' },
    retailPrice: { section: 'productRelatedInformation', field: 'retailPrice' },
    size: { section: 'productRelatedInformation', field: 'productSize' },
    packSize: { section: 'productRelatedInformation', field: 'casePack' },
    
    // VendorRelatedInformation
    vendorNumber: { section: 'vendorRelatedInformation', field: 'vendorNumber' },
    vendorName: { section: 'vendorRelatedInformation', field: 'vendorName' },
    sites: { section: 'vendorRelatedInformation', field: 'sites' },
    
    // VendorFacilityInformation
    siteCode: { section: 'vendorFacilityInformation', field: 'siteCode' },
    siteName: { section: 'vendorFacilityInformation', field: 'siteName' },
    address1: { section: 'vendorFacilityInformation', field: 'address1' },
    address2: { section: 'vendorFacilityInformation', field: 'address2' },
    city: { section: 'vendorFacilityInformation', field: 'city' },
    state: { section: 'vendorFacilityInformation', field: 'state' },
    country: { section: 'vendorFacilityInformation', field: 'country' },
    zipCodePostalCode: { section: 'vendorFacilityInformation', field: 'zipCodePostalCode' }
  };
  
  // If useInitialForMissing is true, we need to process all editable fields, not just those in the response
  if (useInitialForMissing && updateOnlyEditableFields) {
    // Process all editable fields from the field mapping
    Object.entries(fieldMapping).forEach(([flattenedFieldName, mapping]) => {
      const { section, field } = mapping;
      
      // Check if field is editable
      const isEditable = isFieldEditable(section, field, null, specificationType);
      if (!isEditable) {
        return;
      }
      
      // Get value from response or use initial value
      const responseValue = (flattenedResponse as any)[flattenedFieldName];
      let valueToSet;
      
      if (responseValue !== undefined && responseValue !== null) {
        // Use value from response
        valueToSet = responseValue;
      } else if (useInitialForMissing) {
        // Use initial value from MainDetailsFormInitial
        const initialValue = (MainDetailsFormInitial as any)[section]?.[field];
        valueToSet = initialValue ?? '';
        console.log(`🔄 [PayloadUtils] Using initial value for ${section}.${field}:`, valueToSet);
      } else {
        // Skip if no value and not using initial
        return;
      }
      
      // Check if field has been user-modified
      const fieldKey = `${section}.${field}`;
      if (userModifiedFields.has(fieldKey)) {
        console.log(`🚫 [PayloadUtils] Skipping user-modified field: ${fieldKey}`);
        return;
      }
      
      // Update the field - handle personnel objects
      if (mergedForm[section]) {
        const processedValue = mapping.isPersonnel ? extractPersonnelValue(valueToSet) : valueToSet;
        (mergedForm[section] as any)[field] = processedValue;
        updatedFields.push(`${section}.${field}`);
        console.log(`✅ [PayloadUtils] Updated ${section}.${field}:`, processedValue);
      }
    });

    // Handle revisionNotes separately when processing all editable fields
    if (flattenedResponse?.revisionNotes !== undefined && flattenedResponse?.revisionNotes !== null) {
      mergedForm.revisionNotes = String(flattenedResponse?.revisionNotes || '');
      updatedFields?.push('revisionNotes (initial)');
    }
  } else {
    // Original logic: Process only fields in the flattened response
    Object.entries(flattenedResponse)?.forEach(([flattenedFieldName, value]) => {
      // Skip metadata fields and revisionNotes (handled separately)
      if (['productId', 'variantId', 'lastUpdated', 'updatedBy', 'revisionNotes']?.includes(flattenedFieldName)) {
        return;
      }
      
      // Skip if value is undefined or null
      if (value === undefined || value === null) {
        return;
      }
      
      // Get field mapping
      const mapping = fieldMapping[flattenedFieldName];
      if (!mapping) {
        console.warn(`⚠️ [PayloadUtils] No mapping found for field: ${flattenedFieldName}`);
        return;
      }
      
      const { section, field } = mapping;
      
      // Check if field is editable (if updateOnlyEditableFields is true)
      const isEditable = isFieldEditable(section, field, null, specificationType);
      
      // Additional protection for non-editable fields when preserveExistingData is true
      if (preserveExistingData && !isEditable) {
        // Get existing value from current form
        const existingValue = (mergedForm[section] as any)?.[field];
        
        // Always preserve existing non-editable field data if it exists
        // This protects auto-populated fields from being overwritten by empty API responses
        if (existingValue && existingValue !== '') {
          return;
        }
        // If existing value is empty, allow non-editable field to be populated from API
      }
      
      // Skip non-editable fields only if updateOnlyEditableFields is true AND the field already has data
      if (updateOnlyEditableFields && !isEditable) {
        const existingValue = (mergedForm[section] as any)?.[field];
        // Only skip if there's already existing data (to preserve it)
        if (existingValue && existingValue !== '') {
          return;
        }
        // If no existing data, allow non-editable field to be populated from API
      }
      
      // Check if field has been user-modified
      const fieldKey = `${section}.${field}`;
      if (userModifiedFields.has(fieldKey)) {
        return;
      }
      
      // Update the field - handle personnel objects
      if (mergedForm[section]) {
        let processedValue = mapping.isPersonnel ? extractPersonnelValue(value) : value;
        if (field === 'productSize' && flattenedFieldName === 'size') {
          processedValue = resolveProduceRacPricingFieldsFromApiResponse({ size: value }).productSize;
        } else if (field === 'casePack' && flattenedFieldName === 'packSize') {
          processedValue = resolveProduceRacPricingFieldsFromApiResponse({ packSize: value }).casePack;
        } else if (field === 'retailPrice') {
          processedValue = resolveProduceRacPricingFieldsFromApiResponse({ retailPrice: value }).retailPrice;
        }
        (mergedForm[section] as any)[field] = processedValue;
        updatedFields.push(`${section}.${field}`);
      }
    });
  }
  
  // Handle revisionNotes separately (root-level string field)
  if (flattenedResponse?.revisionNotes !== undefined && flattenedResponse?.revisionNotes !== null) {
    mergedForm.revisionNotes = String(flattenedResponse?.revisionNotes || '');
    updatedFields?.push('revisionNotes');
  }

  // Main Details GET is the source of truth for pricing — always sync from API unless user-modified
  const pricingFromResponse = resolveProduceRacPricingFieldsFromApiResponse(flattenedResponse);
  (
    [
      { formField: 'retailPrice' as const, value: pricingFromResponse.retailPrice },
      { formField: 'productSize' as const, value: pricingFromResponse.productSize },
      { formField: 'casePack' as const, value: pricingFromResponse.casePack },
    ] as const
  ).forEach(({ formField, value }) => {
    const fieldKey = `productRelatedInformation.${formField}`;
    if (userModifiedFields.has(fieldKey)) {
      return;
    }
    if (mergedForm.productRelatedInformation) {
      mergedForm.productRelatedInformation[formField] = value;
      updatedFields.push(fieldKey);
    }
  });

  const legacyIncompleteFieldKey = "productRelatedInformation.legacyIncomplete";
  if (
    !userModifiedFields.has(legacyIncompleteFieldKey) &&
    mergedForm.productRelatedInformation
  ) {
    mergedForm.productRelatedInformation.legacyIncomplete = normalizeLegacyIncompleteFromApi(
      flattenedResponse.legacyIncomplete
    );
    updatedFields.push(legacyIncompleteFieldKey);
  }
  
  return mergedForm;
};

/**
 * Helper functions for specific merge scenarios
 */

// For initial load - merge all fields from API response
export const mergeMainDetailsFromInitialLoad = (
  currentForm: IMainDetailsForm,
  flattenedResponse: FlattenedResponseData
): IMainDetailsForm => {
  return mergeMainDetailsFormFromFlattenedResponse(currentForm, flattenedResponse, {
    forceFullUpdate: true,
    preserveExistingData: false,
    onlyEditableFields: false
  });
};

// For updates - merge only editable fields, preserve existing data
export const mergeMainDetailsFromUpdate = (
  currentForm: IMainDetailsForm,
  flattenedResponse: FlattenedResponseData
): IMainDetailsForm => {
  return mergeMainDetailsFormFromFlattenedResponse(currentForm, flattenedResponse, {
    forceFullUpdate: false,
    preserveExistingData: true,
    onlyEditableFields: true
  });
};

// For selective updates - merge any fields that exist in response
export const mergeMainDetailsSelective = (
  currentForm: IMainDetailsForm,
  flattenedResponse: FlattenedResponseData
): IMainDetailsForm => {
  return mergeMainDetailsFormFromFlattenedResponse(currentForm, flattenedResponse, {
    forceFullUpdate: false,
    preserveExistingData: true,
    onlyEditableFields: false
  });
};

// For complete replacement - replace all fields with API response
export const replaceMainDetailsFromResponse = (
  currentForm: IMainDetailsForm,
  flattenedResponse: FlattenedResponseData
): IMainDetailsForm => {
  return mergeMainDetailsFormFromFlattenedResponse(currentForm, flattenedResponse, {
    forceFullUpdate: true,
    preserveExistingData: false,
    onlyEditableFields: false
  });
};

/**
 * Create structured payload containing ONLY editable fields for API saving
 */
export const createStructuredEditablePayload = (
  formData: IMainDetailsForm,
  productId: string | number,
  variantId: string | number
) => {
  console.log('🏗️ [PayloadUtils] Creating structured editable payload from:', formData);
  
  const payload = {
    productId: String(productId),
    variantId: String(variantId),
    tabName: "mainDetails",
    lastUpdated: new Date().toISOString(),
    
    formData: {} as EditableMainDetailsForm,
    
    metadata: {
      updatedBy: "current-user",
      formType: "mainDetails",
      editableFieldsOnly: true
    }
  };
  
  // ProductSpecificationInformation - only editable fields
  const productSpecificationName = formData?.productSpecificationInformation?.productSpecificationName;
  const artworkDeadline = formData?.productSpecificationInformation?.artworkDeadline;
  
  const editableProductSpec: Partial<EditableProductSpecificationInformation> = {};
  
  if (productSpecificationName) {
    editableProductSpec.productSpecificationName = productSpecificationName;
  }
  if (artworkDeadline) {
    editableProductSpec.artworkDeadline = artworkDeadline;
  }
  
  if (Object.keys(editableProductSpec).length > 0) {
    payload.formData.productSpecificationInformation = editableProductSpec as EditableProductSpecificationInformation;
  }
  
  // ProductRelatedInformation - only editable fields
  const editableProductRelated: Partial<EditableProductRelatedInformation> = {};
  
  if (formData?.productRelatedInformation?.weightVolumePackQty) {
    editableProductRelated.weightVolumePackQty = formData.productRelatedInformation.weightVolumePackQty;
  }
  if (formData?.productRelatedInformation?.weightUOM) {
    editableProductRelated.weightUOM = formData.productRelatedInformation.weightUOM;
  }
  if (formData?.productRelatedInformation?.buyer) {
    editableProductRelated.buyer = formData.productRelatedInformation.buyer;
  }
  if (formData?.productRelatedInformation?.trafficManager) {
    editableProductRelated.trafficManager = formData.productRelatedInformation.trafficManager;
  }
  if (formData?.productRelatedInformation?.regulatoryAgenciesOversight !== undefined && formData?.productRelatedInformation?.regulatoryAgenciesOversight !== null) {
    editableProductRelated.regulatoryAgenciesOversight = formatActiveProjectMultiSelectIdsForSave(
      formData.productRelatedInformation.regulatoryAgenciesOversight
    );
  }
  if (formData?.productRelatedInformation?.foodSaftyHaccpPlan) {
    editableProductRelated.foodSaftyHaccpPlan = formData.productRelatedInformation.foodSaftyHaccpPlan;
  }
  if (formData?.productRelatedInformation?.ftlList) {
    editableProductRelated.ftlList = formData.productRelatedInformation.ftlList;
  }
  if (formData?.productRelatedInformation?.warehousesWhereSupplied) {
    editableProductRelated.warehousesWhereSupplied = formatActiveProjectMultiSelectIdsForSave(
      formData.productRelatedInformation.warehousesWhereSupplied
    );
  }
  
  if (Object.keys(editableProductRelated).length > 0) {
    payload.formData.productRelatedInformation = editableProductRelated as EditableProductRelatedInformation;
  }
  
  console.log('✅ [PayloadUtils] Structured editable payload created:', payload);
  
  return payload;
};